import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { ChildFieldDefinition, LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden/Hidden";
import Img from "@insite/mobius/Img";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { TYPOGRAPHY, WIDGETS } from "@tSurya/theme/design";
import useEmblaCarousel from "embla-carousel-react";
import React, { useEffect } from "react";
import { css } from "styled-components";

const W = (WIDGETS as any).subcategoryNav || {};

const enum fields {
    dataSource = "dataSource",
    items = "items",
    categoryFilter = "categoryFilter",
    categoryConfigurations = "categoryConfigurations",
    paddingTop = "paddingTop",
    paddingBottom = "paddingBottom",
    paddingHorizontal = "paddingHorizontal",
    mobilePaddingTop = "mobilePaddingTop",
    mobilePaddingBottom = "mobilePaddingBottom",
    mobilePaddingHorizontal = "mobilePaddingHorizontal",
}

const enum dataSources {
    manual = "manual",
    perCategory = "perCategory",
}

interface ItemFields {
    image: string;
    imageAlt: string;
    label: string;
    anchorId: string;
    link: LinkFieldValue;
}

interface CategoryConfigurationFields {
    name: string;
    enabled: boolean;
    categoryIds: string[];
    items: HasFields[];
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.dataSource]: dataSources;
        [fields.items]: HasFields[];
        [fields.categoryFilter]: string;
        [fields.categoryConfigurations]: HasFields[];
        [fields.paddingTop]: string;
        [fields.paddingBottom]: string;
        [fields.paddingHorizontal]: string;
        [fields.mobilePaddingTop]: string;
        [fields.mobilePaddingBottom]: string;
        [fields.mobilePaddingHorizontal]: string;
    };
}

type Props = OwnProps;

const emptyItems: HasFields[] = [];

const itemFieldDefinitions: ChildFieldDefinition[] = [
    {
        name: "image",
        displayName: "Image",
        editorTemplate: "ImagePickerField",
        defaultValue: "",
    },
    {
        name: "imageAlt",
        displayName: "Image Alt Text",
        editorTemplate: "TextField",
        defaultValue: "",
    },
    {
        name: "label",
        displayName: "Label",
        editorTemplate: "TextField",
        defaultValue: "",
    },
    {
        name: "anchorId",
        displayName: "Anchor ID",
        editorTemplate: "TextField",
        defaultValue: "",
    },
    {
        name: "link",
        displayName: "Link (optional)",
        editorTemplate: "LinkField",
        defaultValue: { type: "Page", value: "" },
    },
];

const normalizeCategoryId = (categoryId: string | undefined) => categoryId?.split(":")[0].toLowerCase() || "";

export interface SurSubcategoryNavStyles {
    main?: InjectableCss;
    desktopRow?: InjectableCss;
    item?: InjectableCss;
    imageWrapper?: InjectableCss;
    label?: InjectableCss;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    mobileImageWrapper?: InjectableCss;
}

export const surSubcategoryNavStyles: SurSubcategoryNavStyles = {
    main: {
        css: css`
            width: 100%;
            padding-top: ${W.paddingTop || "40px"};
            padding-bottom: ${W.paddingBottom || "40px"};
            padding-left: ${W.paddingHorizontal || "0"};
            padding-right: ${W.paddingHorizontal || "0"};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: ${W.mobilePaddingTop || "32px"};
                        padding-bottom: ${W.mobilePaddingBottom || "32px"};
                        padding-left: ${W.mobilePaddingHorizontal || "0"};
                        padding-right: ${W.mobilePaddingHorizontal || "0"};
                    `,
                    css`
                        padding-top: ${W.mobilePaddingTop || "32px"};
                        padding-bottom: ${W.mobilePaddingBottom || "32px"};
                        padding-left: ${W.mobilePaddingHorizontal || "0"};
                        padding-right: ${W.mobilePaddingHorizontal || "0"};
                    `,
                    css`
                        padding-top: ${W.mobilePaddingTop || "32px"};
                        padding-bottom: ${W.mobilePaddingBottom || "32px"};
                        padding-left: ${W.mobilePaddingHorizontal || "0"};
                        padding-right: ${W.mobilePaddingHorizontal || "0"};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    desktopRow: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: flex-start;
            gap: ${W.gap || "20px"};
            width: 100%;
        `,
    },
    item: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: ${W.itemGap || "4px"};
            cursor: pointer;
            text-decoration: none;
            &:hover {
                opacity: 0.8;
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: ${W.imageSize || "172px"};
            height: ${W.imageSize || "172px"};
            overflow: hidden;
            flex-shrink: 0;
            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        `,
    },
    label: {
        css: css`
            font-family: ${W.label?.fontFamily || TYPOGRAPHY.fontFamily.title};
            font-size: ${W.label?.fontSize || "15px"};
            font-weight: ${W.label?.fontWeight || "500"};
            line-height: ${W.label?.lineHeight || "1.5"};
            letter-spacing: ${W.label?.letterSpacing || "23px"};
            color: ${getColor("text.main")};
            text-align: center;
            margin: 4px 0 0 0;
        `,
    },
    carouselWrapper: {
        css: css`
            position: relative;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0 -16px;
                    `,
                    css`
                        margin: 0;
                    `,
                    css`
                        margin: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0 16px;
                    `,
                    css`
                        padding: 0;
                    `,
                    css`
                        padding: 0;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    carouselSlide: {
        css: css`
            position: relative;
            flex: 0 0 auto;
            width: ${W.mobileSlideWidth || "110px"};
            max-width: ${W.mobileSlideWidth || "110px"};
            margin-right: ${W.mobileSlideMargin || "12px"};
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: ${W.itemGap || "4px"};
            cursor: pointer;
        `,
    },
    mobileImageWrapper: {
        css: css`
            width: ${W.mobileImageSize || "100px"};
            height: ${W.mobileImageSize || "100px"};
            overflow: hidden;
            flex-shrink: 0;
            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        `,
    },
};

const styles = surSubcategoryNavStyles;

const itemLinkCss = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${W.itemGap || "4px"};
    text-decoration: none;
    color: inherit;
    cursor: pointer;
    &:hover {
        opacity: 0.8;
    }
`;

const SurSubcategoryNav: React.FC<Props> = ({ fields }) => {
    const category = React.useContext(CategoryContext);
    const usesPerCategoryContent = fields.dataSource === dataSources.perCategory;
    const currentCategoryId = normalizeCategoryId(category?.id);
    const activeConfiguration = usesPerCategoryContent
        ? fields.categoryConfigurations?.find(configuration => {
              const configurationFields = configuration.fields as unknown as CategoryConfigurationFields;
              return (
                  configurationFields.enabled !== false &&
                  configurationFields.categoryIds?.some(categoryId => normalizeCategoryId(categoryId) === currentCategoryId)
              );
          })
        : undefined;
    const activeItems = usesPerCategoryContent
        ? ((activeConfiguration?.fields as unknown as CategoryConfigurationFields | undefined)?.items ?? emptyItems)
        : fields.items || emptyItems;
    const [emblaRef, embla] = useEmblaCarousel();
    const itemLinks = useGetLinks(activeItems, o => o.fields.link);

    useEffect(() => {
        if (!embla) {
            return;
        }
        embla.reInit({
            align: "start",
            loop: false,
            dragFree: true,
        });
    }, [embla, activeItems]);

    // Category filter: if configured, only show on matching categories
    if (!usesPerCategoryContent && fields.categoryFilter) {
        const allowedSegments = fields.categoryFilter.split(",").map(s => s.trim().toLowerCase());
        const currentSegment = category?.urlSegment?.toLowerCase() || "";
        if (!allowedSegments.includes(currentSegment)) {
            return null;
        }
    }

    // CMS padding overrides
    const hasPaddingOverride = !!fields.paddingTop || !!fields.paddingBottom || !!fields.paddingHorizontal;
    const hasMobilePaddingOverride = !!fields.mobilePaddingTop || !!fields.mobilePaddingBottom || !!fields.mobilePaddingHorizontal;
    const containerOverrideCss =
        hasPaddingOverride || hasMobilePaddingOverride
            ? css`
                  ${fields.paddingTop ? `padding-top: ${fields.paddingTop};` : ""}
                  ${fields.paddingBottom ? `padding-bottom: ${fields.paddingBottom};` : ""}
        ${fields.paddingHorizontal ? `padding-left: ${fields.paddingHorizontal}; padding-right: ${fields.paddingHorizontal};` : ""}
        ${hasMobilePaddingOverride
                      ? ({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    ${fields.mobilePaddingTop ? `padding-top: ${fields.mobilePaddingTop};` : ""}
                                    ${fields.mobilePaddingBottom ? `padding-bottom: ${fields.mobilePaddingBottom};` : ""}
                    ${fields.mobilePaddingHorizontal
                                        ? `padding-left: ${fields.mobilePaddingHorizontal}; padding-right: ${fields.mobilePaddingHorizontal};`
                                        : ""}
                                `,
                                css`
                                    ${fields.mobilePaddingTop ? `padding-top: ${fields.mobilePaddingTop};` : ""}
                                    ${fields.mobilePaddingBottom ? `padding-bottom: ${fields.mobilePaddingBottom};` : ""}
                    ${fields.mobilePaddingHorizontal
                                        ? `padding-left: ${fields.mobilePaddingHorizontal}; padding-right: ${fields.mobilePaddingHorizontal};`
                                        : ""}
                                `,
                                css`
                                    ${fields.mobilePaddingTop ? `padding-top: ${fields.mobilePaddingTop};` : ""}
                                    ${fields.mobilePaddingBottom ? `padding-bottom: ${fields.mobilePaddingBottom};` : ""}
                    ${fields.mobilePaddingHorizontal
                                        ? `padding-left: ${fields.mobilePaddingHorizontal}; padding-right: ${fields.mobilePaddingHorizontal};`
                                        : ""}
                                `,
                                null,
                                null,
                            ])
                      : ""}
              `
            : undefined;
    const mainCss = containerOverrideCss
        ? css`
              ${styles.main?.css} ${containerOverrideCss}
          `
        : styles.main?.css;

    const renderItemContent = (item: HasFields, imageWrapperStyle: InjectableCss | undefined) => {
        const itemFields = item.fields as unknown as ItemFields;
        return (
            <>
                <StyledWrapper {...imageWrapperStyle}>
                    <Img src={itemFields.image} altText={itemFields.imageAlt || itemFields.label} />
                </StyledWrapper>
                <StyledWrapper {...styles.label}>{itemFields.label}</StyledWrapper>
            </>
        );
    };

    const renderClickableItem = (item: HasFields, index: number, imageWrapperStyle: InjectableCss | undefined, wrapperStyle?: InjectableCss) => {
        const itemFields = item.fields as unknown as ItemFields;
        const resolvedUrl = itemLinks.links[index]?.url;

        if (resolvedUrl) {
            const linkCss = wrapperStyle?.css
                ? css`
                      ${wrapperStyle.css} ${itemLinkCss}
                  `
                : itemLinkCss;
            return (
                <Link key={index} href={resolvedUrl} css={linkCss}>
                    {renderItemContent(item, imageWrapperStyle)}
                </Link>
            );
        }

        return (
            <StyledWrapper
                key={index}
                {...(wrapperStyle || styles.item)}
                onClick={() => itemFields.anchorId && document.getElementById(itemFields.anchorId)?.scrollIntoView({ behavior: "smooth" })}
            >
                {renderItemContent(item, imageWrapperStyle)}
            </StyledWrapper>
        );
    };

    if (activeItems.length === 0) {
        return null;
    }

    return (
        <StyledWrapper css={mainCss}>
            {/* Desktop: horizontal row, all visible */}
            <Hidden below="lg">
                <StyledWrapper {...styles.desktopRow}>{activeItems.map((item, index) => renderClickableItem(item, index, styles.imageWrapper))}</StyledWrapper>
            </Hidden>

            {/* Mobile: Embla carousel */}
            <Hidden
                above="md"
                css={css`
                    width: 100%;
                `}
            >
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper
                            css={css`
                                display: flex;
                            `}
                        >
                            {activeItems.map((item, index) => renderClickableItem(item, index, styles.mobileImageWrapper, styles.carouselSlide))}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurSubcategoryNav,
    definition: {
        group: "Basic",
        displayName: "Sur Subcategory Nav",
        fieldDefinitions: [
            {
                name: fields.dataSource,
                displayName: "Content Source",
                editorTemplate: "DropDownField",
                defaultValue: dataSources.manual,
                fieldType: "General",
                options: [
                    { displayName: "Manual Items", value: dataSources.manual },
                    { displayName: "Items by Category", value: dataSources.perCategory },
                ],
                sortOrder: 0,
            },
            {
                name: fields.items,
                displayName: "Items",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                isVisible: item => item.fields.dataSource !== dataSources.perCategory,
                getDisplay: (item: HasFields) => {
                    return item.fields.label || "Untitled";
                },
                fieldDefinitions: itemFieldDefinitions,
            },
            {
                name: fields.categoryFilter,
                displayName: "Category Filter (URL Segments)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Comma-separated category URL segments where this widget should appear (e.g. 'All,Living,Dining'). Leave empty to show on all pages.",
                placeholder: "All,Living,Dining",
                sortOrder: 2,
                isVisible: item => item.fields.dataSource !== dataSources.perCategory,
            },
            {
                name: fields.categoryConfigurations,
                displayName: "Category Configurations",
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                tooltip:
                    "The first enabled configuration assigned to the current category is displayed. Categories that are not assigned to a configuration do not show the widget.",
                sortOrder: 3,
                isVisible: item => item.fields.dataSource === dataSources.perCategory,
                getDisplay: (item: HasFields) => {
                    return item.fields.name || "Untitled Configuration";
                },
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Configuration Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "enabled",
                        displayName: "Enabled",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                    {
                        name: "categoryIds",
                        displayName: "Categories",
                        editorTemplate: "CategoriesField",
                        defaultValue: [],
                        tooltip: "Select every category where this configuration should be displayed.",
                    },
                    {
                        name: "items",
                        displayName: "Items",
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields) => {
                            return item.fields.label || "Untitled";
                        },
                        fieldDefinitions: itemFieldDefinitions,
                    },
                ],
            },
            {
                name: fields.paddingTop,
                displayName: "Padding Top (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop top padding (e.g. '40px'). Leave empty for default.",
                placeholder: "40px",
                sortOrder: 50,
            },
            {
                name: fields.paddingBottom,
                displayName: "Padding Bottom (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop bottom padding (e.g. '40px'). Leave empty for default.",
                placeholder: "40px",
                sortOrder: 51,
            },
            {
                name: fields.paddingHorizontal,
                displayName: "Padding Left/Right (Desktop)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override desktop horizontal padding. Leave empty for default.",
                placeholder: "0",
                sortOrder: 52,
            },
            {
                name: fields.mobilePaddingTop,
                displayName: "Padding Top (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile top padding. Leave empty for default.",
                placeholder: "32px",
                sortOrder: 53,
            },
            {
                name: fields.mobilePaddingBottom,
                displayName: "Padding Bottom (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile bottom padding. Leave empty for default.",
                placeholder: "32px",
                sortOrder: 54,
            },
            {
                name: fields.mobilePaddingHorizontal,
                displayName: "Padding Left/Right (Mobile)",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Override mobile horizontal padding. Leave empty for default.",
                placeholder: "0",
                sortOrder: 55,
            },
        ],
    },
};

export default widgetModule;
